import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { CreditCard, AlertTriangle, ArrowRight, X } from 'lucide-react';
import clsx from 'clsx';
import api from '../../utils/api';
import { useAuth } from '../../context/AuthContext';

/**
 * Tells an organisation when its subscription needs attention.
 *
 * Without this, a plan chosen at signup sat unpaid forever with nobody told: the account
 * worked, no invoice existed, and nothing anywhere in the product mentioned payment. Every
 * comparable product — Deputy, Xero — carries a persistent bar with the state and a direct
 * way to act on it, and that is the pattern here.
 *
 * Two rules keep it from becoming noise. It never blocks anything: care work does not stop
 * because a card is missing, and locking a roster over billing would harm participants
 * rather than persuade an administrator. And it is only ever shown to the people who can
 * act on it — a support worker cannot enter a card, so telling them is pure interruption.
 *
 * The page area remounts on every navigation, so the subscription is cached for a few minutes
 * (it used to be fetched again on every page change) and "Dismiss for now" lasts for the browser
 * session instead of reappearing on the next page. A different status shows again.
 */
const CACHE_MS = 5 * 60 * 1000;
let cache = null; // { key, at, sub } — per signed-in user
const DISMISS_KEY = 'rostery.billingNotice.dismissed';

const TONES = {
  amber: {
    box: 'border-amber-200 bg-amber-50', title: 'text-amber-900', body: 'text-amber-800', icon: 'text-amber-600',
  },
  rose: {
    box: 'border-rose-200 bg-rose-50', title: 'text-rose-900', body: 'text-rose-800', icon: 'text-rose-600',
  },
};

export default function BillingNotice() {
  const { user } = useAuth();
  const { pathname } = useLocation();
  const canAct = !!user && (user.role === 'admin' || user.role === 'super_admin');
  const onBilling = pathname.startsWith('/billing');
  const key = user ? String(user.id || user.email || '') : '';
  const fresh = cache && cache.key === key && Date.now() - cache.at < CACHE_MS;

  const [sub, setSub] = useState(() => (fresh ? cache.sub : null));
  const [dismissed, setDismissed] = useState(() => {
    try { return sessionStorage.getItem(DISMISS_KEY) || ''; } catch { return ''; }
  });

  useEffect(() => {
    if (!canAct) return undefined;
    // On the billing page the status is about to change (a card added, a plan paid), so the next
    // page reads it fresh.
    if (onBilling) { cache = null; return undefined; }
    if (cache && cache.key === key && Date.now() - cache.at < CACHE_MS) { setSub(cache.sub); return undefined; }
    let alive = true;
    api.get('/billing/subscription')
      .then(r => {
        const s = r.data?.subscription || null;
        cache = { key, at: Date.now(), sub: s };
        if (alive) setSub(s);
      })
      .catch(() => {});
    return () => { alive = false; };
  }, [canAct, onBilling, key]);

  // Already on the page that fixes it — a banner pointing at the current page is clutter.
  if (!canAct || !sub || onBilling || dismissed === sub.status) return null;

  const NOTICES = {
    incomplete: {
      tone: 'amber',
      icon: CreditCard,
      title: 'Add your payment details',
      body: `Your ${sub.plan_name} plan is active but no payment method is on file. Add one to keep your account running without interruption.`,
      cta: 'Add payment details',
    },
    past_due: {
      tone: 'rose',
      icon: AlertTriangle,
      title: 'Your last payment didn’t go through',
      body: 'Your account is still working normally. Update your card to avoid any interruption.',
      cta: 'Update payment method',
    },
    unpaid: {
      tone: 'rose',
      icon: AlertTriangle,
      title: 'Payment overdue',
      body: 'Several attempts to charge your card have failed. Update it to keep your subscription.',
      cta: 'Update payment method',
    },
  };

  const notice = NOTICES[sub.status];
  if (!notice) return null;   // active, trialing, cancelled — nothing to say

  const Icon = notice.icon;
  const t = TONES[notice.tone];
  const dismiss = () => {
    setDismissed(sub.status);
    try { sessionStorage.setItem(DISMISS_KEY, sub.status); } catch { /* private mode: dismissed for this page only */ }
  };

  return (
    <div className={clsx('flex flex-wrap items-center gap-3 rounded-xl border px-4 py-3 mb-4', t.box)} role="status">
      <Icon size={18} className={clsx('shrink-0', t.icon)} aria-hidden="true" />
      <div className="flex-1 min-w-[200px]">
        <p className={clsx('text-sm font-semibold', t.title)}>{notice.title}</p>
        <p className={clsx('text-[13px] leading-snug', t.body)}>{notice.body}</p>
      </div>
      <Link to="/billing" className="btn-primary btn-sm shrink-0">
        {notice.cta} <ArrowRight size={14} aria-hidden="true" />
      </Link>
      <button
        type="button"
        onClick={dismiss}
        aria-label="Dismiss for now"
        title="Dismiss for now"
        className={clsx('w-9 h-9 inline-flex items-center justify-center rounded-lg shrink-0 transition-colors duration-150 hover:bg-black/5', t.title)}
      >
        <X size={15} aria-hidden="true" />
      </button>
    </div>
  );
}
